import { useEffect, useState } from 'react';
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

import StatisticsService from '../../services/statistics-service';
import useErrorHandler from '../../services/error-handler';

// описывает 4 диаграммы: метрика (косы/датчики) + период (90 дней/год)
const CHARTS = [
    {
        title: 'Потрачено датчиков за год',
        subtitle: 'Один столбик — 7 дней',
        metric: 'sensors',
        period: 'weekly',
        color: '#4c8bf5',
        withYear: true,
    },
    {
        title: 'Потрачено датчиков за 90 дней',
        subtitle: 'Один столбик — 1 день',
        metric: 'sensors',
        period: 'daily',
        color: '#4c8bf5',
        withYear: false,
    },
    {
        title: 'Создано кос за год',
        subtitle: 'Один столбик — 7 дней',
        metric: 'kosas',
        period: 'weekly',
        color: '#66bb6a',
        withYear: true,
    },
    {
        title: 'Создано кос за 90 дней',
        subtitle: 'Один столбик — 1 день',
        metric: 'kosas',
        period: 'daily',
        color: '#66bb6a',
        withYear: false,
    },
];

// 'YYYY-MM-DD' -> 'ДД.ММ' или 'ДД.ММ.ГГ' (для годовых диаграмм)
const shortDate = (iso, withYear) => {
    const [year, month, day] = iso.split('-');
    return withYear ? `${day}.${month}.${year.slice(2)}` : `${day}.${month}`;
};

const StatChart = ({ title, subtitle, points, color, withYear }) => {
    const labels = points.map((p) => p.date);
    const values = points.map((p) => p.value);
    // подписываем не более ~10 меток по оси X, иначе они не читаются
    const step = Math.max(1, Math.ceil(points.length / 10));

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {subtitle}
            </Typography>
            <BarChart
                height={280}
                xAxis={[
                    {
                        scaleType: 'band',
                        data: labels,
                        valueFormatter: (value) => shortDate(value, withYear),
                        tickLabelInterval: (_value, index) => index % step === 0,
                    },
                ]}
                series={[{ data: values, color, valueFormatter: (value) => `${value}` }]}
                margin={{ left: 48, right: 8, top: 8, bottom: 24 }}
            />
        </Paper>
    );
};

const Statistics = () => {
    const pushError = useErrorHandler();

    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const response = await StatisticsService.kosa();
                setStatistics(response.data);
            } catch (e) {
                pushError(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!statistics) return null;

    return (
        <Container maxWidth="xl" sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Статистика производства
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                }}
            >
                {CHARTS.map((chart) => (
                    <StatChart
                        key={`${chart.metric}-${chart.period}`}
                        title={chart.title}
                        subtitle={chart.subtitle}
                        points={statistics[chart.metric][chart.period]}
                        color={chart.color}
                        withYear={chart.withYear}
                    />
                ))}
            </Box>
        </Container>
    );
};

export default Statistics;
